import {permissionObj} from "../../../../Helpers/permission";
import {getDateLongAsIs} from "../../../../Apis/functions";
import {MODELS} from "../../../../Apis/Models";
import {TABLES} from "../../../../Apis/Tables";

/**
 * Tabs of the guards module: the roster of the guards and the zone accesses they are assigned to.
 */
export const GUARDS_TABS = {
    ROSTER: {INDEX: 0, TRANS: 'roster'},
    ZONE_ACCESS: {INDEX: 1, TRANS: 'zone_access'},
};

/**
 * Steps of the creation of a guard. The documents and the fingerprint can be skipped.
 */
export const GUARD_CREATE_STEPS = {
    DETAILS: 0,
    CONTRACT: 1,
    DOCUMENTS: 2,
    PHOTO: 3,
    FINGERPRINT: 4,
    SUMMARY: 5,
};

export const GUARD_CREATE_STEP_LABELS = ['details', 'contract', 'documents', 'add_photo', 'capture_fingerprint', 'summary'];

export const GUARD_OPTIONAL_STEPS = [GUARD_CREATE_STEPS.DOCUMENTS, GUARD_CREATE_STEPS.FINGERPRINT];

/**
 * Tabs of the update of a guard, each one is saved on its own.
 */
export const GUARD_UPDATE_TABS = {
    DETAILS: 0,
    CONTRACT: 1,
    DOCUMENTS: 2,
    PHOTO: 3,
    FINGERPRINT: 4,
};

export const GUARD_UPDATE_TAB_LABELS = ['details', 'contract', 'documents', 'photo', 'fingerprint'];

/**
 * Steps of the creation of a zone access, the update shows the zones and the guards as tabs.
 */
export const ZONE_ACCESS_STEPS = {
    ZONES: 0,
    GUARDS: 1,
    SUMMARY: 2,
};

export const ZONE_ACCESS_STEP_LABELS = ['zones', 'guards', 'summary'];

export const ZONE_ACCESS_UPDATE_TAB_LABELS = ['details', 'guards'];

/**
 * Saved document as the list shows it. The service sends its file apart, and `file` is kept for the
 * pending documents, which carry the one picked to be uploaded.
 */
export const toGuardDocument = (document) => ({
    id: document?.id,
    name: document?.name,
    fileName: document?.file?.userFileNames?.[0],
    filePath: document?.file?.filePath,
});

/**
 * Download path of a saved document of a guard in the file upload service. A pending document has none.
 */
export const guardDocumentPath = (document) => document?.filePath || null;

/**
 * Items a summary section shows, as the summary list card does. The ones left out are shown as a
 * "+N" after them.
 */
export const SUMMARY_VISIBLE_ITEMS = 7;

export const summaryVisibleItems = (items = []) => items.slice(0, SUMMARY_VISIBLE_ITEMS);

export const summaryHiddenItems = (items = []) => items.slice(SUMMARY_VISIBLE_ITEMS);

export const summaryOverflowCount = (items = []) => Math.max(items.length - SUMMARY_VISIBLE_ITEMS, 0);

export const summaryHasOverflow = (items = []) => summaryOverflowCount(items) > 0;

/**
 * Sub-tabs of the zones and the guards on the update of a zone access: the ones to add and the
 * ones already assigned.
 */
export const ZONE_ACCESS_ITEM_TABS = {
    ADD: 0,
    ASSIGNED: 1,
};

/**
 * Permissions of the roster tab, where the guards are managed.
 */
export const GUARD_ROSTER_PERMISSION = {
    READ: permissionObj.WEB_GUARD_ROSTER_READ,
    CREATE: permissionObj.WEB_GUARD_ROSTER_CREATE,
    UPDATE: permissionObj.WEB_GUARD_ROSTER_UPDATE,
    DELETE: permissionObj.WEB_GUARD_ROSTER_DELETE,
    EXPORT: permissionObj.WEB_GUARD_ROSTER_EXPORT,
};

/**
 * Permissions of the buttons of the header by tab: the roster and the zone accesses.
 */
export function TAB_PERMISSION_GUARDS() {
    return {
        CREATE: {
            [GUARDS_TABS.ROSTER.INDEX]: GUARD_ROSTER_PERMISSION.CREATE,
            [GUARDS_TABS.ZONE_ACCESS.INDEX]: ZONE_ACCESS_PERMISSION.CREATE,
        },
        DELETE: {
            [GUARDS_TABS.ROSTER.INDEX]: GUARD_ROSTER_PERMISSION.DELETE,
            [GUARDS_TABS.ZONE_ACCESS.INDEX]: ZONE_ACCESS_PERMISSION.DELETE,
        },
        EXPORT: {
            [GUARDS_TABS.ROSTER.INDEX]: GUARD_ROSTER_PERMISSION.EXPORT,
            [GUARDS_TABS.ZONE_ACCESS.INDEX]: ZONE_ACCESS_PERMISSION.EXPORT,
        },
        // The advanced search has no permission of its own, it comes with the read one of the tab
        ADVANCE_SEARCH: {
            [GUARDS_TABS.ROSTER.INDEX]: GUARD_ROSTER_PERMISSION.READ,
            [GUARDS_TABS.ZONE_ACCESS.INDEX]: ZONE_ACCESS_PERMISSION.READ,
        },
    };
}

/**
 * Model and table each tab is searched and exported with.
 */
export const GUARDS_TAB_SEARCH = {
    [GUARDS_TABS.ROSTER.INDEX]: {MODULE_ID: MODELS.GuardRoster, OPTION: TABLES.GUARDS},
    [GUARDS_TABS.ZONE_ACCESS.INDEX]: {MODULE_ID: MODELS.GuardZoneAccess, OPTION: TABLES.ZONE_ACCESS},
};

/**
 * Permissions of the zone access tab, where the zone accesses of the guards are managed.
 */
export const ZONE_ACCESS_PERMISSION = {
    READ: permissionObj.WEB_GUARD_ZONE_ACCESS_READ,
    CREATE: permissionObj.WEB_GUARD_ZONE_ACCESS_CREATE,
    UPDATE: permissionObj.WEB_GUARD_ZONE_ACCESS_UPDATE,
    DELETE: permissionObj.WEB_GUARD_ZONE_ACCESS_DELETE,
    EXPORT: permissionObj.WEB_GUARD_ZONE_ACCESS_EXPORT,
};

/**
 * Zone access of the guards. Only the name and one zone are required, the guards are optional.
 */
export const ZONE_ACCESS_DATA = {
    /**
     * Body of a zone access. The service receives the ids of its zones and the user ids of its guards.
     *
     * @param {string} name Name of the zone access.
     * @param {Array} zones Zones assigned to it.
     * @param {Array} guards Guards assigned to it.
     */
    set: function (name, zones = [], guards = []) {
        return {
            name: name.trim(),
            zoneIds: zones.map((zone) => zone.id),
            guardIds: guards.map((guard) => guard.userId),
        };
    }
};

/**
 * User of a guard. The email is required once the guard exists, since it is the one it logs in with.
 */
export const GUARD_USER_DATA = {
    id: '',
    userTypeId: '',
    name: '',
    lastName: '',
    secondLastName: '',
    genderId: '',
    phoneNumber: '',
    statusId: '',
    email: '',
    password: '',
    pin: '',
    documentId: '',
    dob: null,
    isValid: function () {
        return this.name !== '' &&
            this.lastName !== '' &&
            (!this.id || (this.email !== '' && this.email != null));
    },
    /**
     * Body of the user of a guard. A saved guard also sends its id and user type, and an empty
     * password leaves the one it has.
     *
     * @param {string} password Password to send, the one typed unless another is given (e.g. a generated one).
     */
    set: function (password = this.password) {
        return {
            dob: getDateLongAsIs(this.dob),
            email: this.email,
            genderId: this.genderId,
            lastName: this.lastName,
            name: this.name,
            phoneNumber: this.phoneNumber
                ? (this.phoneNumber[0] === '+' ? this.phoneNumber : '+' + this.phoneNumber)
                : null,
            password: password || null,
            secondLastName: this.secondLastName,
            statusId: this.statusId,
            documentId: this.documentId || null,
            pin: this.pin || null,
            ...(this.id ? {id: this.id, userTypeId: this.userTypeId} : {}),
        };
    }
};

/**
 * Contract of a guard, kept in its employee record. A guard carries no role, department or zone,
 * and only its start date is required.
 */
export const GUARD_CONTRACT_DATA = {
    id: '',
    employeeId: '',
    startDate: null,
    endDate: null,
    isValid: function () {
        return this.startDate !== null;
    },
    /**
     * Body of the contract of a guard. A contract without end date lasts indefinitely, the service
     * reads it as the start date. A guard carries no role, the service leaves it with the NONE role.
     */
    set: function () {
        return {
            employeeId: this.employeeId || null,
            startDate: getDateLongAsIs(this.startDate),
            endDate: getDateLongAsIs(this.endDate === null ? this.startDate : this.endDate),
            roleId: null,
            ...(this.id ? {id: this.id} : {}),
        };
    }
};
